import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Popconfirm, Select, Space, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import GcPagina from '../components/Layout/GcPagina';
import { CargaFichas } from '../components/Ferias/CargaFichas';
import { Generado } from '../components/Ferias/Generado';
import { dia, fechaHoraMadrid, listaCasos, SIN_DATO } from '../components/Ferias/textos';
import '../components/Ferias/Ferias.css';
import {
  descargarDocumento,
  eliminarCaso,
  ESTADO_ERROR,
  ESTADO_PENDIENTE,
  ESTADO_TRATADO,
  leerDisponibilidadLectura,
  listarCasos,
  listarFerias,
  mensajeError,
} from '../api/feriasApi';
import type { CasoBandeja, Feria, FiltroBandeja, PaginaBandeja, ProgresoLectura } from '../api/feriasApi';
import { claseFilaAtencion, FctAviso, FctEstado, FctGlifoAtencion } from '../fct/react';
import type { FctFamilia, FctNombreIcono } from '../fct/react';

const TAMANIO_PAGINA = 50;

/**
 * Los estados de un caso que muestra la bandeja (M6 §4, P7), con la familia de cada uno. Los demás del
 * catálogo FC_C_FERIAS_ESTADO_CASO (Oportunidad venta, Oportunidad compra y Actividad) ya no se usan: lo
 * generado se ve en los iconos de la fila.
 */
const ESTADOS_CASO: { id: number; nombre: string; familia: FctFamilia }[] = [
  { id: ESTADO_PENDIENTE, nombre: 'Pendiente', familia: 'neutro' },
  { id: ESTADO_TRATADO, nombre: 'Tratado', familia: 'exito' },
  { id: ESTADO_ERROR, nombre: 'Error', familia: 'riesgo' },
];

/** El progreso de lectura de un caso, como lo muestra la bandeja: icono y etiqueta, nunca solo color. */
const LECTURA: Record<ProgresoLectura, { etiqueta: string; familia: FctFamilia; icono: FctNombreIcono }> = {
  esperando: { etiqueta: 'Por leer', familia: 'neutro', icono: 'hora' },
  porVerificar: { etiqueta: 'Por verificar', familia: 'proceso', icono: 'editar' },
  verificado: { etiqueta: 'Verificada', familia: 'exito', icono: 'completado' },
  verificadoNuevaLectura: { etiqueta: 'Nueva lectura por revisar', familia: 'aviso', icono: 'editar' },
  fallida: { etiqueta: 'Lectura fallida', familia: 'riesgo', icono: 'alerta' },
};

// El rango de un selector de fechas como días aaaa-mm-dd, el único formato que manda la bandeja.
function rango(valores: [Dayjs | null, Dayjs | null] | null): [string?, string?] {
  return [valores?.[0]?.format('YYYY-MM-DD'), valores?.[1]?.format('YYYY-MM-DD')];
}

/**
 * La bandeja de fichas de feria (fuente §12, diseño §4 de fct-ferias): los casos de todas las ferias, la
 * entrada más reciente primero, con filtros, los posibles duplicados resaltados y nombrados, el PDF en
 * el visor del navegador y la eliminación tras una confirmación. Recurso Clientes.Altas.Gestionar.
 */
const FeriasPage: React.FC = () => {
  const [ferias, setFerias] = useState<Feria[]>([]);
  const [filtro, setFiltro] = useState<FiltroBandeja>({});
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState<PaginaBandeja | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El fallo de una acción sobre un caso (eliminar, abrir su PDF), aparte del de cargar la bandeja.
  const [errorAccion, setErrorAccion] = useState<{ titulo: string; texto: string } | null>(null);
  const [cargaAbierta, setCargaAbierta] = useState(false);
  const [lecturaActiva, setLecturaActiva] = useState(true);

  // Todas las ferias, activas o no, para el filtro; y si las fichas se leen solas.
  useEffect(() => {
    listarFerias(false)
      .then(setFerias)
      .catch((e: unknown) => setError(mensajeError(e)));
    leerDisponibilidadLectura()
      .then((d) => setLecturaActiva(d.habilitada))
      .catch(() => setLecturaActiva(true));
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await listarCasos(filtro, pagina, TAMANIO_PAGINA));
      setError(null);
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setCargando(false);
    }
  }, [filtro, pagina]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Un filtro nuevo vuelve a la primera página.
  const filtrar = (cambio: Partial<FiltroBandeja>) => {
    setFiltro((actual) => ({ ...actual, ...cambio }));
    setPagina(1);
  };

  const eliminar = async (id: number) => {
    setErrorAccion(null);
    try {
      await eliminarCaso(id);
      await cargar();
    } catch (e) {
      setErrorAccion({ titulo: `No se ha podido eliminar el caso #${id}`, texto: mensajeError(e) });
    }
  };

  // El PDF va con el token, así que no puede ser un enlace directo: se pide como blob y se abre en una
  // pestaña. La pestaña se abre ya en el clic, para que el navegador no la bloquee como ventana emergente.
  const abrirDocumento = async (idCaso: number) => {
    setErrorAccion(null);
    const pestania = window.open('', '_blank');
    try {
      const url = URL.createObjectURL(await descargarDocumento(idCaso));
      if (pestania) {
        pestania.opener = null;
        pestania.location.href = url;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      pestania?.close();
      setErrorAccion({ titulo: `No se ha podido abrir la ficha del caso #${idCaso}`, texto: mensajeError(e) });
    }
  };

  const columnas: ColumnsType<CasoBandeja> = [
    {
      // El número del caso, al que se refieren las marcas de duplicado.
      title: 'Caso',
      dataIndex: 'id',
      className: 'fct-mono',
      render: (id: number) => `#${id}`,
    },
    {
      title: 'Aviso',
      dataIndex: 'aviso',
      className: 'fct-mono',
      render: (aviso: string | null) => aviso ?? SIN_DATO,
    },
    {
      title: 'Reunión',
      dataIndex: 'fechaReunion',
      className: 'fct-num',
      align: 'right',
      render: dia,
    },
    {
      // El código y el nombre del cliente guardado (D-FE-30); su número donde Gescom no está.
      title: 'Cliente',
      key: 'cliente',
      render: (_, caso) =>
        caso.codigoCliente || caso.nombreCliente ? (
          <>
            <span className="fct-mono">{caso.codigoCliente || SIN_DATO}</span>
            <div>{caso.nombreCliente}</div>
          </>
        ) : (
          <span className="fct-mono">{caso.idCliente ?? SIN_DATO}</span>
        ),
    },
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      render: (prioridad: boolean | null) => (prioridad === null ? SIN_DATO : prioridad ? 'Prioritaria' : 'No'),
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, caso) => {
        const familia = ESTADOS_CASO.find((e) => e.id === caso.idEstado)?.familia ?? 'neutro';
        return (
          <Space size="small">
            <FctEstado personalizado={{ etiqueta: caso.estado, familia }} />
            {/* Lo generado desde «Acciones», de los pasos completados del caso (M6 §4). */}
            <Generado avance={caso.avance} />
          </Space>
        );
      },
    },
    {
      title: 'Lectura',
      dataIndex: 'lectura',
      render: (lectura: ProgresoLectura) => <FctEstado personalizado={LECTURA[lectura]} />,
    },
    {
      title: 'Progreso',
      key: 'progreso',
      render: (_, caso) =>
        caso.avance.length === 0 ? SIN_DATO : caso.avance.map((p) => `${p.paso}: ${p.estado.toLowerCase()}`).join(' · '),
    },
    {
      title: 'Entrada',
      dataIndex: 'fechaEntrada',
      className: 'fct-num',
      align: 'right',
      render: (entrada: string) => fechaHoraMadrid(entrada),
    },
    { title: 'Feria', dataIndex: 'feria' },
    {
      title: 'Ficha',
      key: 'ficha',
      render: (_, caso) => (
        <>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => void abrirDocumento(caso.id)}>
            {caso.nombreArchivo}
          </Button>
          {caso.posiblesDuplicados.length > 0 && (
            <div className="ferias-duplicado">
              <FctGlifoAtencion nivel="atencion" /> Posible duplicado de {listaCasos(caso.posiblesDuplicados)}
            </div>
          )}
        </>
      ),
    },
    {
      title: '',
      key: 'acciones',
      render: (_, caso) => (
        <Space>
          {/* La verificación del caso (FERIAS-T08), una vez verificado su cliente (FERIAS-T09) y, con
              cliente, sus acciones (M6 §2). */}
          <Link to={`/ferias/casos/${caso.id}`}>Verificar</Link>
          {(caso.lectura === 'verificado' || caso.lectura === 'verificadoNuevaLectura') && <Link to={`/ferias/casos/${caso.id}/cliente`}>Cliente</Link>}
          {caso.idCliente !== null && <Link to={`/ferias/casos/${caso.id}/acciones`}>Acciones</Link>}
          <Popconfirm
            title={`¿Eliminar el caso #${caso.id}?`}
            description="Sale de la bandeja. Su ficha se conserva y no se toca nada en Gescom."
            okText="Sí, eliminar"
            cancelText="No"
            // Destructivo, no primario: la pantalla conserva un solo primario (manual, regla 2).
            okType="default"
            okButtonProps={{ danger: true }}
            onConfirm={() => eliminar(caso.id)}
          >
            <Button size="small">Eliminar</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <GcPagina
      seccion="Ferias"
      titulo="Bandeja de fichas"
      densidad="compacta"
      contexto={datos ? `${datos.total} ${datos.total === 1 ? 'caso' : 'casos'} · todas las ferias` : 'Todas las ferias'}
      acciones={
        <Button type="primary" onClick={() => setCargaAbierta(true)}>
          Subir fichas
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }} aria-label="Filtros">
        <Select
          aria-label="Feria"
          allowClear
          placeholder="Todas las ferias"
          style={{ minWidth: 200 }}
          options={ferias.map((f) => ({ value: f.id, label: f.nombre }))}
          onChange={(idFeria?: number) => filtrar({ idFeria })}
        />
        <DatePicker.RangePicker
          aria-label="Fecha de entrada"
          placeholder={['Entrada desde', 'hasta']}
          format="DD/MM/YYYY"
          onChange={(valores) => {
            const [entradaDesde, entradaHasta] = rango(valores);
            filtrar({ entradaDesde, entradaHasta });
          }}
        />
        <DatePicker.RangePicker
          aria-label="Fecha de reunión"
          placeholder={['Reunión desde', 'hasta']}
          format="DD/MM/YYYY"
          onChange={(valores) => {
            const [reunionDesde, reunionHasta] = rango(valores);
            filtrar({ reunionDesde, reunionHasta });
          }}
        />
        <Select
          aria-label="Prioridad"
          allowClear
          placeholder="Cualquier prioridad"
          style={{ minWidth: 170 }}
          options={[
            { value: 'true', label: 'Prioritarias' },
            { value: 'false', label: 'No prioritarias' },
          ]}
          onChange={(valor?: string) => filtrar({ prioridad: valor === undefined ? undefined : valor === 'true' })}
        />
        <Select
          aria-label="Estado"
          allowClear
          placeholder="Cualquier estado"
          style={{ minWidth: 180 }}
          options={ESTADOS_CASO.map((e) => ({ value: e.id, label: e.nombre }))}
          onChange={(idEstado?: number) => filtrar({ idEstado })}
        />
        <Select
          aria-label="Lectura"
          allowClear
          placeholder="Cualquier lectura"
          style={{ minWidth: 210 }}
          options={(Object.keys(LECTURA) as ProgresoLectura[]).map((valor) => ({ value: valor, label: LECTURA[valor].etiqueta }))}
          onChange={(lectura?: ProgresoLectura) => filtrar({ lectura })}
        />
      </Space>

      {!lecturaActiva && (
        <FctAviso tipo="aviso" titulo="La lectura automática está desactivada">
          Las fichas no se leen solas. Pueden verificarse completando los campos a mano.
        </FctAviso>
      )}

      {error && (
        <FctAviso tipo="error" titulo="No se ha podido cargar la bandeja">
          {error}
        </FctAviso>
      )}

      {errorAccion && (
        <FctAviso tipo="error" titulo={errorAccion.titulo}>
          {errorAccion.texto}
        </FctAviso>
      )}

      <Table<CasoBandeja>
        rowKey="id"
        size="small"
        loading={cargando}
        columns={columnas}
        dataSource={datos?.elementos ?? []}
        rowClassName={(caso) => claseFilaAtencion(caso.posiblesDuplicados.length > 0 ? 'atencion' : 'normal')}
        locale={{ emptyText: 'No hay fichas con estos filtros.' }}
        pagination={{
          current: pagina,
          pageSize: TAMANIO_PAGINA,
          total: datos?.total ?? 0,
          showSizeChanger: false,
          onChange: setPagina,
        }}
      />

      <CargaFichas abierto={cargaAbierta} onCerrar={() => setCargaAbierta(false)} onCargado={() => void cargar()} />
    </GcPagina>
  );
};

export default FeriasPage;
